import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { planningApi } from "@/domains/Grid/api/planningApi";
import { PlanningGrid } from "@/domains/Grid/PlanningGrid";
import { ClientGridSlot } from "@/domains/Grid/ClientGridSlot";
import { PostCardAuthProvider } from "@/domains/PostCreator/contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";
import type { GridData } from "@/domains/Grid/types/grid.types";

interface ClientPlanningViewProps {
  clientId: string;
  token?: string;
}

export function ClientPlanningView({ clientId, token }: ClientPlanningViewProps) {
  const navigate = useNavigate();
  const [grid, setGrid] = useState<GridData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      return;
    }
    loadActiveGrid();
  }, [clientId, token]);

  const loadActiveGrid = async () => {
    try {
      setLoading(true);
      const grids = await planningApi.listGrids(clientId, true);
      const activeGrid = grids.find((g) => g.isActive);
      setGrid(activeGrid || null);
    } catch (error) {
      console.error("Failed to load active grid:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostClick = (position: number) => {
    navigate(`/client/${clientId}/planning/feed/${position}`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-4" />
        <p>Loading your planning grid...</p>
      </div>
    );
  }

  if (!grid) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
        <h2 className="mb-4 text-gray-900">No Active Planning Grid</h2>
        <p className="text-gray-600">
          Your admin hasn't shared any posts for review yet. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <PostCardAuthProvider
      userRole={UserRole.CLIENT}
      clientId={clientId}
      authToken={token}
    >
      <div className="bg-gray-50 overflow-x-hidden w-full">
        <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4">
          <PlanningGrid
            items={grid.gridItems}
            className="gap-1 md:gap-[4px] w-full box-border"
            renderSlot={(item, position) => (
              <ClientGridSlot
                item={item}
                onClick={() => handlePostClick(position)}
              />
            )}
          />
        </div>
      </div>
    </PostCardAuthProvider>
  );
}
